import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { productsAPI } from '@/utils/api';
import { formatCOP } from '@/utils/format';
import { useCart } from '@/context/CartContext';

const pickSuggestions = (products, items, missing) => {
  const inCart = new Set(items.map((i) => i.variant_id));
  const options = [];
  products.forEach((p) => {
    (p.variants || []).forEach((v) => {
      if (!v.is_available || inCart.has(v.variant_id)) return;
      options.push({ product: p, variant: v, price: v.price_retail_with_tax });
    });
  });
  const covering = options.filter((o) => o.price >= missing).sort((a, b) => a.price - b.price);
  const below = options.filter((o) => o.price < missing).sort((a, b) => b.price - a.price);
  const seen = new Set();
  const unique = (list) => list.filter((o) => {
    if (seen.has(o.product.id)) return false;
    seen.add(o.product.id);
    return true;
  });
  const top = unique(covering).slice(0, 2);
  const rest = unique(below).slice(0, 3 - top.length);
  return [...top, ...rest].slice(0, 3);
};

export const CouponUpsell = ({ coupons, subtotal, items }) => {
  const { addItem } = useCart();
  const [products, setProducts] = useState([]);

  const target = useMemo(() => {
    const locked = (coupons || []).filter((c) => c.status === 'active' && c.min_purchase > subtotal);
    return locked.sort((a, b) => b.discount_value - a.discount_value)[0] || null;
  }, [coupons, subtotal]);

  useEffect(() => {
    if (!target) return;
    productsAPI.getAll({ limit: 200 })
      .then(setProducts)
      .catch(() => setProducts([]));
  }, [!!target]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!target) return null;
  const missing = target.min_purchase - subtotal;
  const suggestions = pickSuggestions(products, items, missing);
  if (suggestions.length === 0) return null;
  const label = target.discount_type === 'percentage' ? `${target.discount_value}%` : formatCOP(target.discount_value);

  return (
    <div className="mt-4 rounded-2xl bg-[#FFF7EE] border border-[#F3D9C4] p-4" data-testid="checkout-coupon-upsell">
      <p className="text-sm font-semibold text-[#1C1917] leading-snug">
        <Sparkles className="inline w-4 h-4 text-[#D47A4A] mr-1 -mt-0.5" />
        Te faltan <span className="text-[#D47A4A]" data-testid="upsell-missing">{formatCOP(missing)}</span> para desbloquear tu <span className="text-[#2A5C43]">{label}</span> con {target.code}
      </p>
      <p className="text-xs text-[#57534E] mt-1 mb-3">Agrega uno de estos y desbloquea el cupón:</p>
      <div className="flex flex-col gap-2">
        {suggestions.map(({ product, variant, price }) => (
          <div key={variant.variant_id} className="flex items-center gap-3 bg-white rounded-xl border border-[#F3D9C4] p-2" data-testid={`upsell-item-${variant.variant_id}`}>
            {product.images?.[0] ? (
              <img src={product.images[0]} alt={product.name} className="w-12 h-12 rounded-lg object-contain bg-[#FAF9F5] shrink-0" />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-[#FAF9F5] shrink-0 flex items-center justify-center text-[10px] text-[#8BA58F] font-semibold">{(product.brand || product.name || '?').slice(0, 2).toUpperCase()}</div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-[#1C1917] truncate">{product.name}</p>
              <p className="text-[11px] text-[#57534E]">{variant.size} · <span className="font-semibold text-[#2A5C43]">{formatCOP(price)}</span></p>
            </div>
            <button
              type="button"
              onClick={() => { addItem(product, variant, 1); toast.success(`${product.name} agregado`); }}
              className="shrink-0 inline-flex items-center gap-1 rounded-full bg-[#D47A4A] hover:bg-[#B86538] text-white text-xs font-semibold px-3 py-1.5 transition-colors"
              data-testid={`upsell-add-${variant.variant_id}`}
            >
              <Plus className="w-3 h-3" /> Agregar
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
